import { useState, useEffect } from 'react';
import { cssUrl } from '../../utils/cssVersion';
const toastStyle = cssUrl('toast.css');


const getCurrentMonthFilter = () => {
  const now = new Date();
  const month = now.toLocaleString('en-US', { month: 'short' });
  const year = now.getFullYear();
  return `${month}${year}`;
};


const useFilterData = ({
  fetchData,
  dataKey,
  searchFields = [],            // strict fields used by chips
  typingSearchFields = null,    // looser fields for keyboard typing; falls back to searchFields
  currentMonthFilter = getCurrentMonthFilter(),
  stylesheetHref = null,
  chipLogic = 'and',
  extraDataKeys = [],
  todayFilterIndex = null,
  typingDebounceMs = 150,
}) => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Two independent search states
  const [chipQuery, setChipQuery] = useState('');
  const [typedQuery, setTypedQuery] = useState('');
  const [debouncedTyped, setDebouncedTyped] = useState('');

  const [selectedMonthYear, setSelectedMonthYear] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isOpenNavigation, setIsOpenNavigation] = useState(false);
  const [extraData, setExtraData] = useState({});
  const [chipsOpen, setChipsOpen] = useState(false);

  // Load external stylesheet
  useEffect(() => {
    if (!stylesheetHref) return;
    const links = [stylesheetHref, toastStyle].map(href => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
      return link;
    });
    return () => links.forEach(link => document.head.removeChild(link));
  }, [stylesheetHref]);

  // Initial load
  useEffect(() => {
    if (!currentMonthFilter) return;
    setSelectedMonthYear(currentMonthFilter);
    getData(currentMonthFilter, false);
  }, [currentMonthFilter]);

  // Debounce typed input so we don't refilter on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTyped(typedQuery), typingDebounceMs);
    return () => clearTimeout(t);
  }, [typedQuery, typingDebounceMs]);

  // Auto-dismiss toast after 3s (mirrors UsersManagement's behaviour)
useEffect(() => {
  if (!toast) return;
  const t = setTimeout(() => setToast(null), 3000);
  return () => clearTimeout(t);
}, [toast]);

  // Combined filter: chips first (strict), then typed text (loose substring)
  useEffect(() => {
    const normalize = (s) => String(s ?? '').toLowerCase().replace(/\s+/g, '');
    const todayDate = new Date().toISOString().split('T')[0];
    const fieldsForTyping = typingSearchFields ?? searchFields;

    let result = items;

    // 1. Chip filter — exact match after whitespace removal, supports AND/OR + 'today'
    const chipQ = chipQuery.toLowerCase().trim();
    if (chipQ) {
      const terms = chipQ.split(',').map(t => t.trim()).filter(Boolean);
      const method = chipLogic === 'or' ? 'some' : 'every';
      result = result.filter(item =>
        terms[method](term => {
          if (term === 'today' && todayFilterIndex !== null) {
            return item[todayFilterIndex] === todayDate;
          }
          const normalizedTerm = normalize(term);
          return searchFields.some(i => normalize(item[i]) === normalizedTerm);
        })
      );
    }

    // 2. Typed filter — partial substring match, AND across whitespace-separated terms
    const typedQ = debouncedTyped.toLowerCase().trim();
    if (typedQ) {
      const terms = typedQ.split(/\s+/).filter(Boolean);
      result = result.filter(item =>
        terms.every(term =>
          fieldsForTyping.some(i =>
            String(item[i] ?? '').toLowerCase().includes(term)
          )
        )
      );
    }

    setFilteredItems(result);
  }, [
    chipQuery,
    debouncedTyped,
    items,
    searchFields,
    typingSearchFields,
    chipLogic,
    todayFilterIndex,
  ]);

  // Core data fetcher
  const getData = async (monthYear, closeNav = true) => {
    if (!monthYear) return;
    setLoading(true);
    try {
      const data = await fetchData({ monthYear });

      if (data.status === 'success') {
        setItems(data[dataKey] || []);
        setTotalCount(data.total || 0);
        const extras = {};
        extraDataKeys.forEach(key => {
          if (data[key]) extras[key] = data[key];
        });
        setExtraData(extras);
        if (closeNav) setIsOpenNavigation(false);
      } else {
        throw new Error(data.error || 'Failed to fetch');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setToast({ message: 'Failed to load data', type: 'error' });
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Calendar month selection
  const handleMonthYearSelect = (value) => {
    setSelectedMonthYear(value);
    getData(value, false);
  };

  const handleNavigationToOpen = () => setIsOpenNavigation(true);
  const handleNavigationToClose = () => setIsOpenNavigation(false);

  const handleCardClick = (item) => setSelectedItem(item);
  const handleModalClose = () => setSelectedItem(null);

  // Submit handler wrapper
  const handleSubmit = async (submitFn, data) => {
    setIsSubmitting(true);
    try {
      const result = await submitFn(data);
      if (result.status === 'success') {
        setToast({ message: 'Updated successfully', type: 'success' });
        setSelectedItem(null);
        getData(selectedMonthYear);
      } else if (result.status === 'partial') {
        setToast({ message: result.message, type: 'warning' });
        setSelectedItem(null);
        getData(selectedMonthYear);
      } else {
        throw new Error(result.error || 'Update failed');
      }
    } catch (err) {
      console.error('Error submitting:', err);
      setToast({ message: 'Failed to update', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear helpers — handy for a "clear all" button
  const clearChips = () => setChipQuery('');
  const clearTyped = () => setTypedQuery('');
  const clearAllFilters = () => { setChipQuery(''); setTypedQuery(''); };

  // Format "Feb2026" → "Feb 2026"
  const getFilterTitle = () => {
    if (!selectedMonthYear) return '';
    const month = selectedMonthYear.slice(0, -4);
    const year = selectedMonthYear.slice(-4);
    return `${month} ${year}`;
  };

  return {
    // Data
    items,
    filteredItems,
    totalCount,
    loading,

    // Search — chips
    chipQuery,
    setChipQuery,
    clearChips,

    // Search — typed
    typedQuery,
    setTypedQuery,
    clearTyped,

    // Combined
    clearAllFilters,
    hasActiveFilters: Boolean(chipQuery || typedQuery),

    // Backwards-compat alias (some components may still reference searchQuery)
    searchQuery: typedQuery,
    setSearchQuery: setTypedQuery,

    // Navigation
    isOpenNavigation,
    selectedMonthYear,
    handleMonthYearSelect,
    handleNavigationToOpen,
    handleNavigationToClose,
    chipsOpen,
    setChipsOpen,

    // Modal
    selectedItem,
    isSubmitting,
    handleCardClick,
    handleModalClose,
    handleSubmit,

    // Toast
    toast,
    setToast,

    // Helpers
    getFilterTitle,
    getData,
    extraData,
  };
};

export { getCurrentMonthFilter };
export default useFilterData;